// biome-ignore-all lint/complexity/noVoid: react-hook-form's trigger() promise is not awaited on purpose
import {
  ClipboardEvent,
  FC,
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ZodArray, ZodObject, ZodString } from "zod";
import entries from "lodash/entries";
import groupBy from "lodash/groupBy";
import { cn } from "~/utils";
import { Button } from "~/components/button/button";
import { Chip } from "./chip";

export interface IMultipleSelectChipProps {
  values: string[];
  placeholder?: string;
  className?: string;
  chipClassName?: string;
  chipInputClassName?: string;
  showErrors?: boolean;
  schema?: ZodObject<{ values: ZodArray<ZodObject<{ value: ZodString }>> }>;

  onValueChange(values: string[]): void;
}

export const MultipleSelectChip: FC<IMultipleSelectChipProps> = ({
  onValueChange,
  values,
  placeholder,
  className = "",
  chipClassName = "",
  chipInputClassName = "",
  schema,
  showErrors,
}) => {
  const {
    watch,
    register,
    control,
    setFocus,
    formState,
    getValues,
    setValue,
    trigger,
  } = useForm({
    defaultValues: {
      values: values.map((value) => ({ value })),
    },
    resolver: schema ? zodResolver(schema) : undefined,
    shouldFocusError: false,
    mode: "onChange",
    reValidateMode: "onChange",
  });
  const multiSelectRef = useRef<HTMLDivElement>(null);

  const current = watch("values", []);

  const array = useFieldArray({
    name: "values",
    control,
  });

  const fields = current.map((value, index) => ({
    ...array.fields[index],
    ...value,
  }));

  useEffect(() => {
    void trigger();
  }, [trigger, current]);

  useEffect(() => {
    // Update values if they change from outside
    setValue(
      "values",
      values.map((v) => ({ value: v })),
    );
  }, [values, setValue]);

  const [active, setActive] = useState<number | null>(null);

  const add = () => {
    array.append({ value: "" }, { shouldFocus: true });
    setTimeout(() => {
      const multiSelectElement = multiSelectRef.current;
      multiSelectElement?.scrollTo?.({
        top: multiSelectElement.scrollHeight,
        behavior: "smooth",
      });
    }, 100);
  };

  const onSubmit = useCallback(() => {
    // Trigger to activate validation
    void trigger();
    return onValueChange(getValues().values.map((v) => v.value));
  }, [getValues, onValueChange, trigger]);

  const onKeyDown = async (
    event: KeyboardEvent<HTMLInputElement>,
    index: number,
  ) => {
    const { code } = event;

    const value = current[index];

    if (["Delete", "Backspace"].includes(code) && !value?.value) {
      event.preventDefault();
      array.remove(index);
      setFocus(`values.${Math.max(0, index - 1)}.value`);
      onSubmit();
      return;
    }

    if (event.key === "Enter" || event.code === "Enter") {
      event.preventDefault();
      event.stopPropagation();
      onSubmit();
      return;
    }

    const addNew = ["Space", "Semicolon", "Comma"].includes(code);

    if (!addNew) {
      return;
    }

    event.preventDefault();

    if (!value?.value) {
      return;
    }

    add();
  };

  const onRemove = async (index: number) => {
    array.remove(index);

    onSubmit();
  };

  const onPaste = (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();

    const text = event.clipboardData.getData("text");
    const stripped = text
      .split(/[ ,;\n]+/)
      .map((x) => x.replaceAll(/\s+/g, ""))
      .filter((x) => x !== "");

    if (!stripped.length) {
      return;
    }

    setActive(null);

    array.append(
      stripped.map((value) => ({ value })),
      {
        shouldFocus: false,
      },
    );
  };

  useEffect(() => {
    if (active !== null) {
      return;
    }

    onSubmit();
  }, [active, onSubmit]);

  const onBlur = () => {
    setActive(null);
  };

  const onFocus = (index: number) => {
    setActive(index);
  };

  const errors = useMemo(() => {
    if (!showErrors) {
      return null;
    }

    const mapped = fields.map(({ value }, index) => {
      const error = formState.errors?.values?.[index]?.value;

      return {
        value,
        error,
      };
    });

    const errored = mapped.filter(({ error }) => error);

    const grouped = groupBy(errored, "error.message");

    return entries(grouped);
  }, [showErrors, fields, formState]);

  return (
    <div
      className="w-full h-full overflow-auto"
      data-testid="multiple-select-chip"
      ref={multiSelectRef}
    >
      <div className={cn("flex flex-wrap", className)}>
        {fields.map(({ id, value }, index) => {
          const error = formState.errors?.values?.[index]?.value;

          return (
            <Chip
              key={id}
              title={value}
              {...register(`values.${index}.value`)}
              error={error?.message}
              onFocus={() => onFocus(index)}
              onRemove={() => onRemove(index)}
              onBlur={() => onBlur()}
              onKeyDown={(event) => onKeyDown(event, index)}
              onPaste={onPaste}
              className={chipClassName}
              inputClassName={chipInputClassName}
            />
          );
        })}

        <Button
          variant="ghost"
          onClick={add}
          className="whitespace-nowrap w-auto"
        >
          {placeholder}
        </Button>
      </div>

      {Boolean(errors?.length) && (
        <div className="mt-5">
          <b>Errors</b> <br />
          {errors?.map(([error, errored]) => (
            <div key={error}>
              <span>{error} </span>
              <small>({errored.map((v) => v.value).join(", ")})</small>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
